import React, { Suspense, lazy } from "react";
import { Switch, useLocation, Route, Redirect } from "react-router-dom";
import qs from "querystring-es3";
import RestApi from "./components/Services/RestApi";
import BugsnagManager from "./BugsnagManager";
import { TABS as PAYG_TABS } from "./components/ReservationPages/PayAsYouGo/payg_routes";
import withGoogleServices from "./components/hoc/withGoogleServices";
import PageLoader from "./components/Common/PageLoader";
import "./index.scss";
import { getAirportClientFromPath } from "./components/ReservationPages/Airport/helpers";
import lazyRetry from "./lazy-retry";
import {
  isPurchaseSuccessPath,
  isSsoLocation,
  shouldRestartSsoSignIn,
} from "./components/ExternalReservationPages/ssoConfig";

import { resolveBuynowRedirect } from "./redirect-config";
import {
  hasRedirectedToLazIo,
  redirectAirportIfNeeded,
} from "./redirectBootstrap";

const NotFoundPage = lazy(() =>
  lazyRetry(() => import("./components/NotFoundPage"), "NotFoundPage")
);

const BasePage = lazy(() =>
  lazyRetry(() => import("./components/Layout/BasePage"), "BasePage")
);

const BasePaygPage = lazy(() =>
  lazyRetry(() => import("./components/Layout/BasePaygPage"), "BasePaygPage")
);

const BaseAirportPage = lazy(() =>
  lazyRetry(
    () => import("./components/ReservationPages/Airport/BaseAirportPage"),
    "BaseAirportPage"
  )
);

const PayAsYouGo = lazy(() =>
  lazyRetry(
    () => import("./components/ReservationPages/PayAsYouGo"),
    "PayAsYouGo"
  )
);

const Lookup = lazy(() =>
  lazyRetry(() => import("./components/LoginPages/Lookup"), "Lookup")
);

const SearchResults = lazy(() =>
  lazyRetry(
    () => import("./components/ReservationPages/SearchResults"),
    "SearchResults"
  )
);

const Airport = lazy(() =>
  lazyRetry(() => import("./components/ReservationPages/Airport"), "Airport")
);

const ChangePassword = lazy(() =>
  lazyRetry(
    () => import("./components/ExternalReservationPages/ChangePassword"),
    "ChangePassword"
  )
);

const PaymentMethods = lazy(() =>
  lazyRetry(
    () => import("./components/AccountPages/PaymentMethods"),
    "PaymentMethods"
  )
);

const SubNow = lazy(() =>
  lazyRetry(() => import("./components/ReservationPages/SubNow"), "SubNow")
);

const Vehicles = lazy(() =>
  lazyRetry(() => import("./components/AccountPages/Vehicles"), "Vehicles")
);

const Subscriptions = lazy(() =>
  lazyRetry(
    () => import("./components/AccountPages/Subscriptions"),
    "Subscriptions"
  )
);

const Transactions = lazy(() =>
  lazyRetry(
    () => import("./components/AccountPages/Transactions"),
    "Transactions"
  )
);

const ExternalBuyNow = lazy(() =>
  lazyRetry(
    () => import("./components/ExternalReservationPages/ExternalBuyNow"),
    "ExternalBuyNow"
  )
);

const SSOCallback = lazy(() =>
  lazyRetry(
    () => import("./components/ExternalReservationPages/SSOCallback"),
    "SSOCallback"
  )
);

const BuyNow = lazy(() =>
  lazyRetry(() => import("./components/ReservationPages/BuyNow"), "BuyNow")
);

const AccountAccess = lazy(() =>
  lazyRetry(
    () => import("./components/LoginPages/AccountAccess"),
    "AccountAccess"
  )
);

const Waitlist = lazy(() =>
  lazyRetry(
    () => import("./components/LoginPages/Fulfillments/Waitlist"),
    "Waitlist"
  )
);

const LocationStandAlone = lazy(() =>
  lazyRetry(
    () => import("./components/ReservationPages/Segment/LocationStandAlone"),
    "LocationStandAlone"
  )
);

const Activation = lazy(() =>
  lazyRetry(() => import("./components/LoginPages/Activation"), "Activation")
);

const Summary = lazy(() =>
  lazyRetry(() => import("./components/AccountPages/Summary"), "Summary")
);

const Profile = lazy(() =>
  lazyRetry(() => import("./components/AccountPages/Profile"), "Profile")
);

const Search = lazy(() =>
  lazyRetry(() => import("./components/ReservationPages/Search"), "Search")
);

const Help = lazy(() =>
  lazyRetry(() => import("./components/AccountPages/Help"), "Help")
);

const IEElevenError = lazy(() =>
  lazyRetry(
    () => import("./components/AccountPages/IEElevenError"),
    "IEElevenError"
  )
);

const CookiesError = lazy(() =>
  lazyRetry(
    () => import("./components/AccountPages/CookiesError"),
    "CookiesError"
  )
);

const Invite = lazy(() =>
  lazyRetry(
    () => import("./components/LoginPages/Fulfillments/Invite"),
    "Invite"
  )
);

const RewardDetails = lazy(() =>
  lazyRetry(
    () => import("./components/AccountPages/RewardDetails"),
    "RewardDetails"
  )
);

const ProgramsAndRewards = lazy(() =>
  lazyRetry(
    () => import("./components/AccountPages/ProgramsAndRewards"),
    "ProgramsAndRewards"
  )
);

const airportClients = [
  "flydenver",
  "mbsairport",
  "flycae",
  "flyacv",
  "lgb",
  "flymso",
  "flyazo",
  "flymot",
  "flymht",
  "flypvd",
  "flybzn",
  "flybhm",
  "flyflo",
  "flyrdd",
  "flypns",
  "flyama",
  "flystl",
  "flyyyj",
  "flyege",
];
// The laz.io redirect target used to be resolved here, by a getTargetDomain()
// that matched hostnames by substring - includes("stg."), includes("uat") - and
// fell back to DOMAIN_MAP[REACT_APP_ENVIRONMENT] for anything else. On
// go.lazparking.ca that fallback resolved to https://laz.io, so the backstops
// below sent Canadian users to the US app. Both now call
// resolveBuynowRedirect, which resolves host, country and origin together
// through the strict allowlist in redirect-config.js. Do not reintroduce a
// substring resolver here.

// List of routes that uses the page layout
// listed here to Switch between layouts
// depending on the current pathname
const loginPages = ["/login", "/fulfill", "/activate", "/waitlist"];
const publicPages = [
  "/lookup",
  "/search",
  "/search-results",
  "/search-results-sub",
  "/hello",
  "/hello/#resetpassword",
  "/echangepassword",
  "/hello#buynow",
  "/hello/#buynow",
  "/buynow",
  "/subnow",
  "/transactions",
  "/help",
  "/",
  ...airportClients.map((client) => {
    return client.replace("/", "");
  }),
];

const loggedInPages = [
  "/login",
  "/summary",
  "/profile",
  "/subscriptions",
  "/payment-methods",
  "/vehicles",
  "/transactions",
  "/rewards",
  "/buynow",
  "/help",
  ...airportClients.map((client) => {
    return client.replace("/", "");
  }),
];
const externalPages = [
  "/elogin",
  "/ebuynow",
  "/etransactions",
  "/ehelp",
  "/echangepassword",
  "/epayment-methods",
  "/evehicles",
];

const reload = () => window.location.reload();

function checkIfAirport() {
  return airportClients.includes(getAirportClientFromPath());
}

function formatQueryParamsFromInvalidHashLocation() {
  const location = window.location.href;
  if (location.includes("hello") && location.includes("#buynow")) {
    const params =
      window.location.search || location.split("#buynow")?.[1] || "";
    return params;
  } else {
    return window.location.search;
  }
}

const contains = (pages, fullpath) => {
  const pathname = "/" + fullpath.split("/")[1];
  return pages.indexOf(pathname) > -1;
};

const restrictedUrlRedirectBeforeLogin = (contextRoot) => {
  let includesExternalPage = false;
  let urlMatch = "";
  const currentUrl = window.location.href;
  externalPages.forEach((page) => {
    if (currentUrl.includes(page)) {
      urlMatch = page.replace("/", "");
      includesExternalPage = true;
    }
  });
  if (includesExternalPage) {
    return (
      <>
        <Route
          path={[contextRoot + "/login", contextRoot + "/elogin"]}
          component={AccountAccess}
        />
        <Redirect to={contextRoot + "/login?type=login&redirect=" + urlMatch} />
      </>
    );
  } else {
    return (
      <>
        <Route path={contextRoot + "/"} component={Search} />
        <Redirect to={contextRoot + "/"} />
      </>
    );
  }
};

function Routes() {
  const location = useLocation();
  const contextRoot = process.env.REACT_APP_CONTEXT_ROOT || "";
  const params =
    location.search !== ""
      ? location.search.split("?")[1]
      : location.hash.split("?")[1];
  let values = {};
  try {
    values = qs.parse(params);
  } catch (error) {
    BugsnagManager.notify(error, {
      context: "Unable to parse params in Routes.js",
    });
    console.warn(error);
  }

  if (location.pathname === contextRoot + "/auth/sso/callback") {
    return (
      <Suspense fallback={<PageLoader />}>
        <Switch>
          <Route
            exact
            path={contextRoot + "/auth/sso/callback"}
            component={SSOCallback}
          />
        </Switch>
      </Suspense>
    );
  }

  function isPayAsYouGo() {
    const pathname = window.location.pathname.split("/").filter((x) => x)[0];

    function stripPathName(path) {
      return path.replace("/", "");
    }

    return (
      pathname === stripPathName(PAYG_TABS.START) ||
      pathname === stripPathName(PAYG_TABS.DASHBOARD) ||
      pathname === stripPathName(PAYG_TABS.ACTIVITY)
    );
  }

  const isAirport = checkIfAirport();

  // Cookies Check
  if (
    location.pathname != null &&
    location.pathname.indexOf("/cookieserror") > -1
  ) {
    return (
      <Suspense fallback={<PageLoader />}>
        <BasePage>
          <Route
            path={contextRoot + "/cookieserror"}
            component={CookiesError}
          />
        </BasePage>
      </Suspense>
    );
  }

  try {
    localStorage.getItem("persist:root");
  } catch (error) {
    window.location.href = contextRoot + "/cookieserror";
  }

  if (
    location.pathname != null &&
    location.pathname.indexOf("/location-details") > -1
  ) {
    return (
      <Suspense fallback={<PageLoader />}>
        <BasePage>
          <Route
            path={contextRoot + "/location-details/:locationId"}
            component={LocationStandAlone}
          />
        </BasePage>
      </Suspense>
    );
  }

  // ie11 check
  let ie11 = !!window.MSInputMethodContext && !!document.documentMode;
  if (!localStorage.getItem("ie11") && ie11) {
    localStorage.setItem("ie11", true);
    window.location.href = contextRoot + "/ieerror";
  }

  if (location?.hash.includes("#resetpassword")) {
    location.pathname = contextRoot + "/echangepassword";
  }

  if (location.pathname?.indexOf?.("/health") > -1) {
    return "true";
  } else if (location.pathname?.indexOf?.("/Pass") > -1) {
    return <Route path="/Pass.html" onEnter={reload} />;
  } else if (location.pathname?.indexOf?.("/ieerror") > -1) {
    return (
      <Suspense fallback={<PageLoader />}>
        <BasePage>
          <Route path={contextRoot + "/ieerror"} component={IEElevenError} />
        </BasePage>
      </Suspense>
    );
  } else if (isPayAsYouGo()) {
    return (
      <Suspense fallback={<PageLoader />}>
        <BasePaygPage>
          <Switch>
            <Route path={PAYG_TABS.START} component={PayAsYouGo} />
            <Route path={PAYG_TABS.DASHBOARD} component={PayAsYouGo} />
            <Route path={PAYG_TABS.ACTIVITY} component={PayAsYouGo} />
          </Switch>
        </BasePaygPage>
      </Suspense>
    );
  } else if (isAirport) {
    // Backstop for the client roots that moved to the new app. The pre-render
    // bootstrap runs once at module evaluation and never reruns, so a
    // client-side push from /flycae/park back to /flycae would otherwise stay
    // here on the legacy app.
    //
    // Goes through the strict host allowlist in redirect-config.js, never a
    // substring resolver - see the note above the route tree. Deep paths,
    // non-migrated clients and ?rd=false all decline and render the legacy page
    // as before.
    if (redirectAirportIfNeeded()) return null;

    return (
      <Suspense fallback={<PageLoader />}>
        <Switch>
          <Route
            path={[
              contextRoot + `/${getAirportClientFromPath()}`,
              contextRoot + `/${getAirportClientFromPath()}/embed`,
            ]}
          >
            {(props) => {
              const isEmbed =
                props.match.path === `/${getAirportClientFromPath()}/embed`;
              return (
                <BaseAirportPage
                  isEmbed={isEmbed}
                  client={getAirportClientFromPath()}
                >
                  <Airport {...props} />
                </BaseAirportPage>
              );
            }}
          </Route>
          <Route path="*" component={NotFoundPage} />
        </Switch>
      </Suspense>
    );
  } else if (contains(loginPages, location.pathname.replace(contextRoot, ""))) {
    return (
      <Suspense fallback={<PageLoader />}>
        <BasePage>
          <Switch>
            <Route
              path={[contextRoot + "/login", contextRoot + "/elogin"]}
              component={AccountAccess}
            />
            <Route path={contextRoot + "/fulfill"} component={Invite} />
            <Route path={contextRoot + "/waitlist"} component={Waitlist} />
            <Route path={contextRoot + "/activate"} component={Activation} />
          </Switch>
        </BasePage>
      </Suspense>
    );
  } else if (
    contains(publicPages, location.pathname.replace(contextRoot, ""))
  ) {
    return (
      <Suspense fallback={<PageLoader />}>
        <BasePage>
          <Switch>
            <Route
              path={[contextRoot + "/lookup", contextRoot + "/lookup/*"]}
              component={Lookup}
            />
            <Route
              path={[
                contextRoot + "/transactions",
                contextRoot + "/etransactions",
                contextRoot + "/transactions/*",
                contextRoot + "/etransactions/*",
              ]}
              component={Transactions}
            />
            <Route path={contextRoot + "/search"} component={Search} />
            <Route
              path={[
                contextRoot + "/search-results/:searchTerm/map",
                contextRoot + "/search-results/:searchTerm",
                contextRoot + "/search-results-sub/:searchTerm/map",
                contextRoot + "/search-results-sub/:searchTerm",
              ]}
              component={withGoogleServices(SearchResults)}
            />
            <Route path={contextRoot + "/hello/#resetpassword"}>
              <Redirect
                to={
                  contextRoot +
                  "/echangepassword" +
                  formatQueryParamsFromInvalidHashLocation()
                }
              />
            </Route>
            <Route
              path={contextRoot + "/echangepassword"}
              component={ChangePassword}
            />
            <Route path={contextRoot + "/hello"}>
              <Redirect
                to={
                  contextRoot +
                  "/buynow" +
                  formatQueryParamsFromInvalidHashLocation()
                }
              />
            </Route>
            <Route path={[contextRoot + "/buynow", contextRoot + "/ebuynow"]}>
              {(props) => {
                // Check for new-app redirect conditions. Host, country and
                // target origin resolve together in redirect-config.js, so this
                // backstop cannot disagree with the Express 301 or the
                // pre-render bootstrap about where a URL lands.
                const params = new URLSearchParams(window.location.search);
                const redirectTarget = resolveBuynowRedirect(
                  window.location.hostname,
                  props.location.pathname.replace(contextRoot, ""),
                  params
                );

                // The bootstrap already called location.replace(). That queues
                // a navigation but the current script keeps running, and
                // `href =` below is a history PUSH that would supersede the
                // replace - putting /buynow back in history and reintroducing
                // the back-button trap.
                if (hasRedirectedToLazIo()) return null;

                if (redirectTarget) {
                  window.location.href = redirectTarget;
                  return null;
                }

                // A completed purchase deliberately clears its own session, and
                // the receipt lives only in BuyNow's component state. Every
                // handoff to ExternalBuyNow below unmounts BuyNow and restarts
                // SSO sign-in, so the confirmation screen opts out of all of
                // them.
                const isOnPurchaseSuccess = isPurchaseSuccessPath(
                  props.location.pathname,
                  contextRoot
                );

                const isTokenExpired = RestApi.checkTokenExpiration(
                  values?.token
                );

                // The rewrite scrubs a spent token from the address bar before
                // handing off to sign-in. There is no handoff on the
                // confirmation screen, so rewriting the receipt's URL under the
                // customer serves no purpose.
                if (isTokenExpired && !isOnPurchaseSuccess) {
                  const lVal = params.get("l");
                  const newUrl =
                    window.location.pathname + (lVal ? "?l=" + lVal : "");
                  window.history.replaceState({}, document.title, newUrl);
                }

                // For SSO locations, an expired stored token is not a live
                // session: presence alone (isSessionValid) would mount the
                // authenticated BuyNow with a dead token and surface a
                // "Session expired" error on a fresh sign-in.
                // checkTokenExpiration decodes the JWT's exp claim locally
                // (no network, no server round-trip) and clears the stale
                // token when it is past exp. Non-SSO buynow visits are
                // unaffected because the check short-circuits behind
                // isSsoLocation and the no-URL-token guard.
                const hasStoredSsoToken =
                  isSsoLocation(values?.l) &&
                  !values?.token &&
                  RestApi.isSessionValid();
                const isStoredSsoTokenExpired =
                  hasStoredSsoToken &&
                  RestApi.checkTokenExpiration(RestApi.getAccessToken());

                if (isStoredSsoTokenExpired) {
                  // Visible confirmation that the expired-token guard fired,
                  // so a return-to-SSO-location-after-logout restarts sign-in
                  // instead of showing a session error. Token value not logged.
                  console.info(
                    "[SSO] Stored access token for location " +
                      (values?.l ?? "unknown") +
                      " is expired/invalid; cleared it and restarting sign-in " +
                      "instead of mounting the authenticated page."
                  );
                }

                if (
                  !isOnPurchaseSuccess &&
                  (shouldRestartSsoSignIn({
                    locationId: values?.l,
                    urlToken: values?.token,
                    hasValidSession: RestApi.isSessionValid(),
                    isStoredTokenExpired: isStoredSsoTokenExpired,
                    pathname: props.location.pathname,
                    contextRoot,
                  }) ||
                    isTokenExpired ||
                    values.key)
                ) {
                  return (
                    <ExternalBuyNow
                      {...props}
                      isTokenExpired={isTokenExpired}
                    />
                  );
                } else {
                  return <BuyNow {...props} />;
                }
              }}
            </Route>
            <Route path={contextRoot + "/subnow"} component={SubNow} />
            <Route
              path={[contextRoot + "/help", contextRoot + "/ehelp"]}
              component={Help}
            />
            <Route path={contextRoot + "/"} component={Search} />
          </Switch>
        </BasePage>
      </Suspense>
    );
  } else if (
    RestApi.isSessionValid() &&
    contains(loggedInPages, location.pathname.replace(contextRoot, ""))
  ) {
    return (
      <Suspense fallback={<PageLoader />}>
        <BasePage>
          <Switch>
            <Route path={contextRoot + "/summary"} component={Summary} />
            <Route path={contextRoot + "/profile"} component={Profile} />
            <Route
              path={[
                contextRoot + "/transactions",
                contextRoot + "/etransactions",
                contextRoot + "/transactions/*",
                contextRoot + "/etransactions/*",
              ]}
              component={Transactions}
            />
            <Route
              path={contextRoot + "/subscriptions"}
              component={Subscriptions}
            />
            <Route
              path={[
                contextRoot + "/payment-methods",
                contextRoot + "/epayment-methods",
              ]}
              component={PaymentMethods}
            />
            <Route
              path={[contextRoot + "/vehicles", contextRoot + "/evehicles"]}
              component={Vehicles}
            />
            <Route
              path={contextRoot + "/rewards/reward-programs/:id"}
              component={RewardDetails}
            />
            <Route
              path={contextRoot + "/rewards"}
              component={ProgramsAndRewards}
            />
            <Route path={contextRoot + "/hello"}>
              <Redirect
                to={
                  contextRoot +
                  "/buynow" +
                  formatQueryParamsFromInvalidHashLocation()
                }
              />
            </Route>
            <Route path={[contextRoot + "/buynow", contextRoot + "/ebuynow"]}>
              {(props) => {
                // Check for new-app redirect conditions. Host, country and
                // target origin resolve together in redirect-config.js, so this
                // backstop cannot disagree with the Express 301 or the
                // pre-render bootstrap about where a URL lands.
                const params = new URLSearchParams(window.location.search);
                const redirectTarget = resolveBuynowRedirect(
                  window.location.hostname,
                  props.location.pathname.replace(contextRoot, ""),
                  params
                );

                // The bootstrap already called location.replace(). That queues
                // a navigation but the current script keeps running, and
                // `href =` below is a history PUSH that would supersede the
                // replace - putting /buynow back in history and reintroducing
                // the back-button trap.
                if (hasRedirectedToLazIo()) return null;

                if (redirectTarget) {
                  window.location.href = redirectTarget;
                  return null;
                }

                // Currently unreachable for buynow paths -- contains() buckets
                // them into publicPages, which is evaluated first -- but it
                // carries the same latent unmount bug, so it gets the same
                // guard and the two copies cannot drift.
                if (
                  !isPurchaseSuccessPath(props.location.pathname, contextRoot) &&
                  (shouldRestartSsoSignIn({
                    locationId: values?.l,
                    urlToken: values?.token,
                    hasValidSession: RestApi.isSessionValid(),
                    pathname: props.location.pathname,
                    contextRoot,
                  }) ||
                    values.key)
                ) {
                  return <ExternalBuyNow {...props} />;
                } else {
                  return <BuyNow {...props} />;
                }
              }}
            </Route>
            <Route
              path={[contextRoot + "/help", contextRoot + "/ehelp"]}
              component={Help}
            />
          </Switch>
        </BasePage>
      </Suspense>
    );
  }
  return (
    <Suspense fallback={<PageLoader />}>
      <BasePage>
        <Switch>{restrictedUrlRedirectBeforeLogin(contextRoot)}</Switch>
      </BasePage>
    </Suspense>
  );
}

export default Routes;
